import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { ArrowCounterClockwise, PencilSimple, Plus } from '@phosphor-icons/react';
import { useEpisodeStore } from '@/state/episodeStore';
import { putStyleSettings } from '@/services/styleSettingsApi';
import { listStylePresets } from '@/services/stylePresetsApi';
import {
  clonePromptTemplate,
  listPromptTemplates,
} from '@/services/promptTemplatesApi';
import { runMutation } from '@/state/saveQueue';
import { toast } from '@/state/toastStore';
import {
  type AspectRatio,
  type ImageQuality,
  type PromptTemplate,
  type StylePreset,
  type StyleSettings,
  type TemplateKind,
  SYSTEM_IMAGE_TEMPLATE_ID,
  SYSTEM_VIDEO_TEMPLATE_ID,
} from '@/types';
import { TemplateEditorDrawer } from './TemplateEditorDrawer';

const ASPECTS: AspectRatio[] = ['16:9', '9:16', '1:1', '4:3', '3:4', '4:5'];
const QUALITIES: ImageQuality[] = ['1K', '2K', '4K'];

const SYSTEM_ID_BY_KIND: Record<TemplateKind, string> = {
  image: SYSTEM_IMAGE_TEMPLATE_ID,
  video: SYSTEM_VIDEO_TEMPLATE_ID,
};

export function StyleTab() {
  const episode = useEpisodeStore((s) => s.episode);
  const setEpisode = useEpisodeStore((s) => s.setEpisode);

  const [presets, setPresets] = useState<StylePreset[]>([]);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [editing, setEditing] = useState<PromptTemplate | null>(null);

  // Load presets + templates whenever the episode changes.
  useEffect(() => {
    if (!episode?.uuid) return;
    let cancelled = false;
    void listStylePresets()
      .then((p) => !cancelled && setPresets(p))
      .catch(() => undefined);
    void listPromptTemplates(episode.uuid)
      .then((t) => !cancelled && setTemplates(t))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [episode?.uuid]);

  const update = useCallback(
    async (next: Partial<StyleSettings>) => {
      if (!episode) return;
      const merged: StyleSettings = { ...episode.styleSettings, ...next };
      const previous = episode.styleSettings;
      setEpisode({ ...episode, styleSettings: merged });
      try {
        const saved = await runMutation(
          {
            episodeUuid: episode.uuid,
            endpoint: '/v2/style-settings',
            method: 'POST',
            body: merged,
          },
          () => putStyleSettings(episode.uuid, merged),
        );
        setEpisode({ ...episode, styleSettings: saved });
      } catch (err) {
        setEpisode({ ...episode, styleSettings: previous });
        toast.err(err instanceof Error ? err.message : 'Failed to save style.');
      }
    },
    [episode, setEpisode],
  );

  if (!episode) return null;
  const style = episode.styleSettings;

  function templatesOfKind(kind: TemplateKind): PromptTemplate[] {
    return templates.filter((t) => t.kind === kind);
  }

  function activeTemplate(kind: TemplateKind): PromptTemplate | null {
    const activeId =
      kind === 'image' ? style.activeImageTemplateId : style.activeVideoTemplateId;
    const id = activeId ?? SYSTEM_ID_BY_KIND[kind];
    return templates.find((t) => t.id === id) ?? null;
  }

  async function selectTemplate(kind: TemplateKind, id: string | null) {
    const patch: Partial<StyleSettings> =
      kind === 'image'
        ? { activeImageTemplateId: id }
        : { activeVideoTemplateId: id };
    await update(patch);
  }

  /**
   * Opens the editor on the active template. If active is the system default,
   * clone first (server-side, atomically activates the new row) and edit the
   * clone — keeps system rows pristine.
   */
  async function editTemplate(kind: TemplateKind) {
    const active = activeTemplate(kind);
    if (!active || !episode) return;
    if (active.isSystem) {
      try {
        const cloned = await clonePromptTemplate(episode.uuid, active.id);
        setTemplates((prev) => [...prev, cloned]);
        // Mirror the server's activation locally so the dropdown updates
        // before the next style-settings fetch.
        await selectTemplate(kind, cloned.id);
        setEditing(cloned);
      } catch (err) {
        toast.err(err instanceof Error ? err.message : 'Clone failed');
      }
      return;
    }
    setEditing(active);
  }

  async function resetTemplate(kind: TemplateKind) {
    await selectTemplate(kind, null);
  }

  function onSaved(next: PromptTemplate) {
    setTemplates((prev) => prev.map((t) => (t.id === next.id ? next : t)));
    setEditing(null);
  }

  return (
    <div className="space-y-6">
      <Field label="Style preset">
        <Select
          value={style.presetId ?? 'custom'}
          onChange={(v) => update({ presetId: v === 'custom' ? null : v })}
          options={[
            { value: 'custom', label: 'Custom' },
            ...presets.map((p) => ({
              value: p.id,
              label: p.isDefault ? `${p.name} (default)` : p.name,
            })),
          ]}
        />
        {style.presetId && (
          <Hint>{presets.find((p) => p.id === style.presetId)?.description ?? ''}</Hint>
        )}
      </Field>

      {!style.presetId && <CustomStyleEditor settings={style} onChange={update} />}

      <Field label="Aspect ratio">
        <SegmentedRow
          value={style.aspectRatio}
          options={ASPECTS}
          onChange={(v) => update({ aspectRatio: v })}
        />
      </Field>

      <Field label="Image quality">
        <SegmentedRow
          value={style.imageQuality}
          options={QUALITIES}
          onChange={(v) => update({ imageQuality: v })}
        />
      </Field>

      <ToggleField
        label="Cultural guidelines"
        on={style.culturalGuidelines}
        onChange={(v) => update({ culturalGuidelines: v })}
        hint="Bosnian/Muslim children's-show conventions filled into the {cultural_guidelines} placeholder of the active image template."
      />

      <TemplateCard
        kind="image"
        active={activeTemplate('image')}
        options={templatesOfKind('image')}
        onSelect={(id) => selectTemplate('image', id)}
        onEdit={() => editTemplate('image')}
        onReset={() => resetTemplate('image')}
      />
      <TemplateCard
        kind="video"
        active={activeTemplate('video')}
        options={templatesOfKind('video')}
        onSelect={(id) => selectTemplate('video', id)}
        onEdit={() => editTemplate('video')}
        onReset={() => resetTemplate('video')}
      />

      {editing && episode && (
        <TemplateEditorDrawer
          episodeUuid={episode.uuid}
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function TemplateCard({
  kind,
  active,
  options,
  onSelect,
  onEdit,
  onReset,
}: {
  kind: TemplateKind;
  active: PromptTemplate | null;
  options: PromptTemplate[];
  onSelect: (id: string | null) => void;
  onEdit: () => void;
  onReset: () => void;
}) {
  const isSystem = active?.isSystem ?? true;
  const previewLines = (active?.body ?? '').split('\n').slice(0, 2).join('\n');

  return (
    <div className="rounded-md bg-surface-2 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
          {kind === 'image' ? 'Image template' : 'Video template'}
        </p>
        {isSystem ? (
          <span className="rounded-sm bg-info/15 px-1.5 py-0.5 font-mono text-[10px] uppercase text-info">
            default
          </span>
        ) : (
          <span className="rounded-sm bg-warn/15 px-1.5 py-0.5 font-mono text-[10px] uppercase text-warn">
            custom
          </span>
        )}
      </div>

      <select
        value={active?.id ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          // Picking "default" maps to NULL on the active slot — the backend
          // composer falls through to the system row by ID lookup.
          onSelect(v === SYSTEM_ID_BY_KIND[kind] ? null : v);
        }}
        className="w-full rounded-md bg-surface-inset px-3 py-2 text-[13px] text-text-1 outline-none ring-1 ring-line-2 focus:ring-line-3"
      >
        {options.map((t) => (
          <option key={t.id} value={t.id}>
            {t.isSystem ? `Default (${kind})` : t.name}
          </option>
        ))}
      </select>

      {previewLines && (
        <p className="mt-2 line-clamp-2 font-mono text-[11px] text-text-3">
          {previewLines}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded-md bg-surface-3 px-2.5 py-1.5 text-[12px] font-medium text-text-1 transition-colors hover:brightness-110"
        >
          {isSystem ? <Plus size={12} weight="bold" /> : <PencilSimple size={12} weight="bold" />}
          {isSystem ? 'New from default' : 'Edit'}
        </button>
        {!isSystem && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium text-text-3 transition-colors hover:bg-surface-3 hover:text-text-1"
          >
            <ArrowCounterClockwise size={12} />
            Reset to default
          </button>
        )}
      </div>
    </div>
  );
}

function CustomStyleEditor({
  settings,
  onChange,
}: {
  settings: StyleSettings;
  onChange: (next: Partial<StyleSettings>) => void;
}) {
  const [draftDescription, setDraftDescription] = useState(
    settings.customStyle?.description ?? '',
  );

  function save() {
    onChange({
      customStyle: {
        image: settings.customStyle?.image ?? '',
        description: draftDescription,
      },
    });
  }

  return (
    <Field label="Custom style description">
      <textarea
        value={draftDescription}
        onChange={(e) => setDraftDescription(e.target.value)}
        onBlur={save}
        placeholder="e.g. retro 70s painted style, soft pastel palette, slight grain"
        rows={3}
        className="w-full resize-none rounded-md bg-surface-inset px-3 py-2 text-[13px] text-text-1 placeholder:text-text-4 outline-none ring-1 ring-line-2 transition-shadow focus:ring-line-3"
      />
    </Field>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
        {label}
      </p>
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-[11px] text-text-4">{children}</p>;
}

function Select<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      disabled={disabled}
      className="w-full rounded-md bg-surface-2 px-3 py-2 text-[13px] text-text-1 outline-none ring-1 ring-line-2 transition-shadow focus:ring-line-3 disabled:cursor-not-allowed disabled:text-text-4"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function SegmentedRow<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly T[];
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-md bg-surface-2 p-1">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={clsx(
            'flex-1 rounded-sm px-3 py-1.5 text-[12px] font-medium tabular-nums transition-colors',
            value === opt
              ? 'bg-accent-soft text-accent'
              : 'text-text-3 hover:text-text-2',
          )}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function ToggleField({
  label,
  hint,
  on,
  onChange,
}: {
  label: string;
  hint?: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={() => onChange(!on)}
        className="flex w-full items-center gap-3"
      >
        <span
          className={clsx(
            'relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors',
            on ? 'bg-accent' : 'bg-surface-3',
          )}
        >
          <span
            className={clsx(
              'absolute h-3 w-3 rounded-full bg-text-on-accent transition-transform',
              on ? 'translate-x-3.5' : 'translate-x-0.5',
            )}
          />
        </span>
        <span className="text-[13px] font-medium text-text-1">{label}</span>
      </button>
      {hint && <p className="mt-1 pl-10 text-[11px] text-text-4">{hint}</p>}
    </div>
  );
}
